// The client's half of lockstep: every tick, say what you want to do (maybe
// nothing). The server closes tick T only when every seat has submitted for T,
// so a client that never submits freezes the match — and the client used to send
// a message type (`match:intent`) the server has no handler for, so no order
// ever reached it either.
//
// Pacing: submit tick 0 at match start, then after each applied frame T wait one
// tick interval and submit T+1. A slow seat slows everyone (the server's
// deadline skips a seat that is gone); a fast one is held to the tick rate.

export interface TickSubmitterOptions {
  /** Send one tick's batch (maybe empty) to the match-server. */
  send: (tick: number, actions: readonly unknown[]) => void;
  intervalMs: number;
  /** Injectable for tests. */
  setTimer?: (fn: () => void, ms: number) => unknown;
  clearTimer?: (handle: unknown) => void;
}

export class TickSubmitter {
  private buffer: unknown[] = [];
  private submitted = -1;
  private timer: unknown = null;
  private stopped = false;
  private readonly setTimer: (fn: () => void, ms: number) => unknown;
  private readonly clearTimer: (handle: unknown) => void;

  constructor(private readonly opts: TickSubmitterOptions) {
    this.setTimer = opts.setTimer ?? ((fn, ms) => setTimeout(fn, ms));
    this.clearTimer = opts.clearTimer ?? ((h) => clearTimeout(h as ReturnType<typeof setTimeout>));
  }

  /** An order from the player; it rides the next submission. */
  queue(intent: unknown): void {
    this.buffer.push(intent);
  }

  /** The match has started: open tick 0. */
  start(): void {
    if (this.submitted < 0) this.submit(0);
  }

  /** Frame `tick` was applied: open the next tick after one interval. */
  onFrameApplied(tick: number): void {
    if (this.stopped) return;
    if (this.timer !== null) this.clearTimer(this.timer);
    this.timer = this.setTimer(() => {
      this.timer = null;
      this.submitIfDue(tick + 1);
    }, this.opts.intervalMs);
  }

  /** After a rejoin replay: open the tick after the last one applied, now. */
  resume(lastApplied: number): void {
    if (this.timer !== null) this.clearTimer(this.timer);
    this.timer = null;
    this.submitIfDue(lastApplied + 1);
  }

  stop(): void {
    this.stopped = true;
    if (this.timer !== null) this.clearTimer(this.timer);
    this.timer = null;
  }

  /** Never twice for one tick: a second submission would replace the first
   *  and drop the orders already sent. */
  private submitIfDue(tick: number): void {
    if (this.stopped || tick <= this.submitted) return;
    this.submit(tick);
  }

  private submit(tick: number): void {
    const actions = this.buffer;
    this.buffer = [];
    this.submitted = tick;
    this.opts.send(tick, actions);
  }
}
